import { useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  type Node,
  type Edge,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { ProcessNode, ActorNode, DataNode } from './nodes/index';
import { DualLabelEdge } from './edges/DualLabelEdge';
import type { FlowNodeDef, FlowEdgeDef } from '@/data/systemFlows/index';

// ============================================================================
// Custom node/edge type maps
// ============================================================================

const nodeTypes = {
  process: ProcessNode,
  actor: ActorNode,
  data: DataNode,
};

const edgeTypes = {
  dualLabel: DualLabelEdge,
};

// ============================================================================
// Conversion helpers
// ============================================================================

function toReactFlowNodes(defs: FlowNodeDef[]): Node[] {
  return defs.map((def) => ({
    id: def.id,
    type: def.type,
    position: def.position,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    data: {
      label: def.label,
      description: def.description,
      domain: def.domain,
    },
  }));
}

function toReactFlowEdges(defs: FlowEdgeDef[]): Edge[] {
  return defs.map((def) => ({
    id: def.id,
    source: def.from,
    target: def.to,
    type: 'dualLabel',
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#d1d5db',
    },
    data: {
      sourceLabel: def.sourceLabel,
      targetLabel: def.targetLabel,
      logic: def.logic,
      condition: def.condition,
    },
  }));
}

// ============================================================================
// Inner diagram (must be inside ReactFlowProvider)
// ============================================================================

interface FlowDiagramProps {
  nodes: FlowNodeDef[];
  edges: FlowEdgeDef[];
  title?: string;
  description?: string;
}

function FlowDiagramInner({ nodes, edges }: FlowDiagramProps) {
  const rfNodes = useMemo(() => toReactFlowNodes(nodes), [nodes]);
  const rfEdges = useMemo(() => toReactFlowEdges(edges), [edges]);

  return (
    <ReactFlow
      nodes={rfNodes}
      edges={rfEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      minZoom={0.2}
      maxZoom={2}
      attributionPosition="bottom-right"
      style={{ width: '100%', height: '100%' }}
    >
      <Background color="#e5e7eb" gap={16} />
      <Controls />
    </ReactFlow>
  );
}

// ============================================================================
// Exported wrapper (provides ReactFlowProvider)
// ============================================================================

export function FlowDiagram(props: FlowDiagramProps) {
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <FlowDiagramInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
